import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import IdsHideFocusMixin from '../../mixins/ids-hide-focus-mixin/ids-hide-focus-mixin';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsSelectionMixin from '../../mixins/ids-selection-mixin/ids-selection-mixin';
import IdsBox from '../ids-box/ids-box';

import '../ids-hyperlink/ids-hyperlink';
import '../ids-checkbox/ids-checkbox';
import styles from './ids-card.scss';
import type IdsHyperlink from '../ids-hyperlink/ids-hyperlink';
import type IdsCheckbox from '../ids-checkbox/ids-checkbox';
import { IdsColorValue } from '../../utils/ids-color-utils/ids-color-utils';
import IdsDraggableMixin from '../../mixins/ids-draggable-mixin/ids-draggable-mixin';
import type IdsDataGrid from '../ids-data-grid/ids-data-grid';

const Base = IdsHideFocusMixin(
  IdsDraggableMixin(
    IdsEventsMixin(
      IdsSelectionMixin(
        IdsBox
      )
    )
  )
);

/**
 * IDS Card Component
 * @type {IdsCard}
 * @inherits IdsBox
 * @mixes IdsEventsMixin
 * @mixes IdsSelectionMixin
 * @mixes IdsHideFocusMixin
 * @part card - the card element
 * @part header - the header element
 * @part content - the card content element
 */
@customElement('ids-card')
@scss(styles)
export default class IdsCard extends Base {
  #clonedCard: any = null;

  #positionId: string | null = null;

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#setFooterClass();
    this.#handleEvents();
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes() {
    return [
      ...super.attributes,
      attributes.ACTIONABLE,
      attributes.AUTO_FIT,
      attributes.AUTO_HEIGHT,
      attributes.HEIGHT,
      attributes.HREF,
      attributes.NO_HEADER,
      attributes.OVERFLOW,
      attributes.TARGET,
      attributes.BACKGROUND_COLOR,
      attributes.WIDTH,
      attributes.DRAG_WIDTH,
      attributes.DRAG_HEIGHT,
      attributes.DRAG_BG_COLOR,
      attributes.DROPPED,
      attributes.DROP_WIDTH,
      attributes.DROP_HEIGHT,
      attributes.DROP_BG_COLOR,
      attributes.FIXED,
      attributes.STACKED,
    ];
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    super.attributeChangedCallback(name, oldValue, newValue);

    if (oldValue === newValue) return;

    if (name === attributes.BACKGROUND_COLOR) this.#setCssVar('--ids-card-color-background', newValue);
    if (name === attributes.WIDTH) this.#setCssVar('--ids-card-width', newValue);

    if (name === attributes.DRAG_WIDTH) this.#setCssVar('--ids-card-width-dragged', newValue);
    if (name === attributes.DRAG_HEIGHT) this.#setCssVar('--ids-card-height-dragged', newValue);
    if (name === attributes.DRAG_BG_COLOR) this.#setCssVar('--ids-card-color-background-dragged', newValue);

    if (name === attributes.DROP_WIDTH) this.#setCssVar('--ids-card-width-dropped', newValue);
    if (name === attributes.DROP_HEIGHT) this.#setCssVar('--ids-card-height-dropped', newValue);
    if (name === attributes.DROP_BG_COLOR) this.#setCssVar('--ids-card-color-background-dropped', newValue);
  }

  /**
   * Method for card template
   * @returns {string} html
   */
  cardTemplate() {
    const html = `
      <div class="ids-card" part="card">
        <div class="ids-card-body${this.selection === 'multiple' ? ' has-checkbox' : ''}">
          <div class="ids-card-header" part="header">
            <slot name="card-header"></slot>
          </div>
          <div class="ids-card-subheader" part="subheader">
            <slot name="card-subheader"></slot>
          </div>
          <div class="ids-card-content${this.overflow === ' hidden' ? ' overflow-hidden' : ''}" part="content">
            <slot name="card-content"></slot>
          </div>
          <div class="ids-card-checkbox${this.selection === 'multiple' ? '' : ' hidden'}">
            <ids-checkbox></ids-checkbox>
          </div>
          <div class="ids-card-footer" part="footer">
            <slot name="card-footer"></slot>
          </div>
        </div>
      </div>
    `;

    return html;
  }

  /**
   * Method for actionable button card template
   * @returns {string} html
   */
  actionableButtonTemplate() {
    const html = `
      <div class="ids-card" part="card">
        <ids-button>
          <slot name="card-content"></slot>
        </ids-button>
      </div>
    `;

    return html;
  }

  /**
   * Method for actionable link card template
   * @returns {string} html
   */
  actionableLinkTemplate() {
    const html = `
      <div class="ids-card" part="card">
        <ids-hyperlink href="${this.href}" target="${this.target}">
          <slot name="card-content"></slot>
        </ids-hyperlink>
      </div>
    `;

    return html;
  }

  /**
   * Inner template contents
   * @returns {string} The template
   */
  template() {
    if (this.actionable) {
      return this.href ? this.actionableLinkTemplate() : this.actionableButtonTemplate();
    }

    return this.cardTemplate();
  }

  /**
   * Establish Internal Event Handlers
   * @private
   * @returns {object} The object for chaining.
   */
  #handleEvents() {
    this.offEvent('click.ids-card', this);
    this.onEvent('click.ids-card', this, this.#handleSelectionChange);

    if (this.selection === 'multiple') {
      const checkbox = this.container?.querySelector<IdsCheckbox>('ids-checkbox');
      this.onEvent('click.checkbox', checkbox, (e: any) => {
        e.stopPropagation();
        e.preventDefault();
        this.#handleMultipleSelectionChange(e);
      });
    }

    const cardContentSlot = this.shadowRoot?.querySelector<HTMLSlotElement>(`slot[name="card-content"]`);

    this.offEvent('slotchange.ids-card', cardContentSlot);
    this.onEvent('slotchange.ids-card', cardContentSlot, () => {
      const dataGrid = this?.querySelector<IdsDataGrid>('ids-data-grid');
      const cardContent = this?.container?.querySelector('.ids-card-content');
      cardContent?.classList.toggle('has-data-grid', !!dataGrid);
    });

    this.offEvent('drag.ids-card', this);
    this.onEvent('drag.ids-card', this, (e: any) => {
      const { translateX = 0, translateY = 0 } = e.detail;
      if (translateX === 0 && translateY === 0) {
        return;
      }

      this.#handleDragStart();
    });

    this.offEvent('dragend.ids-card', this);
    this.onEvent('dragend.ids-card', this, (e: any) => {
      const { translateX = 0, translateY = 0 } = e.detail;
      if (translateX === 0 && translateY === 0) {
        return;
      }

      this.#handleDragend();
    });

    return this;
  }

  /**
   * Handle drag start event of a card
   */
  #handleDragStart() {
    this.removeAttribute(attributes.DROPPED);
    this.setAttribute(attributes.IS_DRAGGING, 'true');

    this.container?.classList?.add('is-dragging');
    if (!this.disabled && !this.#clonedCard && this.fixed) {
      const clonedCard = this.cloneNode(true) as IdsCard;

      // Created cloned element with disabled state with initial position
      clonedCard.style.transform = `translate(0px, 0px)`;
      clonedCard.disabled = true;
      clonedCard.container?.classList?.remove('is-dragging');
      clonedCard.removeAttribute(attributes.IS_DRAGGING);

      this.#clonedCard = clonedCard;
      this.parentNode?.insertBefore(clonedCard, this.nextSibling);
    }

    this.container?.classList?.toggle('is-overlapping', this.#isOverlapping());
  }

  /**
   * Check if the card is overlapping with other cards
   * @returns {boolean} true if the card is overlapping with other cards else false
   */
  #isOverlapping() {
    if (!this.container) return false;

    if (!this.stacked) {
      const currentPosition = this.container.getBoundingClientRect();
      const positions = IdsCard.droppedPositions.filter((p: any) => p?.id !== this.#positionId);
      return positions.some((position: any) => {
        const isOverlappingX = position.left < currentPosition.right && position.right > currentPosition.left;
        const isOverlappingY = position.top < currentPosition.bottom && position.bottom > currentPosition.top;

        return isOverlappingX && isOverlappingY;
      });
    }
    return false;
  }

  static droppedPositions: any[] = [];

  /**
   * Handle drag end event of a card
   */
  #handleDragend() {
    if (!this.container) return;

    const currentPosition = this.container.getBoundingClientRect();

    // If dropped target element is present, get the position of the dropped element
    const dropPosition = this?.droppedTargetElement?.getBoundingClientRect();

    // If the card is not dropped in the target element, reset the card position
    const leftValid = currentPosition?.left >= (dropPosition?.left || 0);
    const rightValid = currentPosition?.right <= (dropPosition?.right || 0);
    const bottomValid = currentPosition?.bottom >= (dropPosition?.top || 0);
    const topValid = currentPosition.top <= (dropPosition?.bottom || 0);

    const xAxisValid = leftValid && rightValid;
    const yAxisValid = bottomValid && topValid;

    let resetPosition = false;
    if (!this.droppedTargetElement || !xAxisValid || !yAxisValid) {
      resetPosition = true;
    }

    // If card stacking is disabled and the card is overlapping with other cards, reset the card position
    if (!this.stacked && !resetPosition) {
      resetPosition = this.#isOverlapping();
    }

    this.removeAttribute(attributes.IS_DRAGGING);
    this.container?.classList?.remove('is-dragging');

    if (resetPosition) {
      this.style.transform = `translate(0px, 0px)`;

      if (this.#clonedCard) {
        this.#clonedCard.remove();
        this.#clonedCard = null;
      }

      this.container?.classList?.remove('is-overlapping');
      this.removeAttribute(attributes.DROPPED);
      this.#removePosition();
      return;
    }

    this.setAttribute(attributes.DROPPED, 'true');
    if (this.#clonedCard) {
      if (this.hasAttribute(attributes.DROP_BG_COLOR)) {
        this.#clonedCard.setAttribute(attributes.DROP_BG_COLOR, this.getAttribute(attributes.DROP_BG_COLOR));
      }
      this.#clonedCard.disabled = false;
    }

    this.#updatePosition();
  }

  #removePosition() {
    IdsCard.droppedPositions = IdsCard.droppedPositions.filter((position: any) => position.id !== this.#positionId);
  }

  #updatePosition() {
    if (!this.container) return;

    const containerPosition = this.container.getBoundingClientRect();
    this.#positionId = !this.#positionId ? `position-${new Date().getTime()}` : this.#positionId;

    const position = {
      left: containerPosition.left,
      right: containerPosition.right,
      bottom: containerPosition.bottom,
      top: containerPosition.top,
      width: containerPosition.width,
      height: containerPosition.height,
      x: containerPosition.x,
      y: containerPosition.y,
      id: this.#positionId,
      totalX: containerPosition.x + containerPosition.width,
      totalY: containerPosition.y + containerPosition.height,
    };

    const index = IdsCard.droppedPositions.findIndex((pos: any) => pos.id === this.#positionId);

    if (index !== -1) {
      IdsCard.droppedPositions[index] = position;
      return;
    }

    IdsCard.droppedPositions.push(position);
  }

  /**
   * Get the dropped target element
   * @returns {Element | null} the element that serves as the drop target
   */
  get droppedTargetElement() {
    return document.querySelector(`#${this.dropTarget}`);
  }

  /**
   * Set css class for footer
   * @private
   * @returns {void}
   */
  #setFooterClass(): void {
    const footerSlot = this.querySelector('[slot="card-footer"]');
    this.container?.classList[footerSlot ? 'add' : 'remove']('has-footer');
    if (footerSlot) {
      const noPadding = footerSlot.hasAttribute(attributes.NO_PADDING);
      const footer = this.container?.querySelector('.ids-card-footer');
      footer?.classList.toggle(attributes.NO_PADDING, noPadding);
    }
  }

  /**
   * Handle single/multiple selection change
   * @private
   * @param {object} e Actual event
   */
  #handleSelectionChange(e: Event) {
    if (this.selection === 'single') {
      this.#handleSingleSelectionChange(e);
    } else if (this.selection === 'multiple') {
      this.#handleMultipleSelectionChange(e);
    }
  }

  /**
   * Change single selection for cards
   * @private
   * @param {object} e Actual event
   */
  #handleSingleSelectionChange(e: Event) {
    if (this.disabled) return;

    const cardElements = document.querySelectorAll('ids-card[selection="single"]');
    [...cardElements].forEach((elem) => elem.setAttribute(attributes.SELECTED, 'false'));
    this.setAttribute(attributes.SELECTED, 'true');

    this.triggerEvent('selectionchanged', this, {
      detail: {
        elem: this,
        nativeEvent: e,
        selected: this.selected,
        selection: this.selection,
      }
    });
  }

  /**
   * Change multiple selection for cards
   * @private
   * @param {object} e Actual event
   */
  #handleMultipleSelectionChange(e: Event) {
    if (this.disabled) return;

    this.container?.querySelector('ids-checkbox')?.setAttribute(attributes.CHECKED, String(this.selected !== true));
    this.setAttribute(attributes.SELECTED, String(this.selected !== true));

    this.triggerEvent('selectionchanged', this, {
      detail: {
        elem: this,
        nativeEvent: e,
        selected: this.selected,
        selection: this.selection,
      }
    });
  }

  /**
   * Redraw the template when some properties change.
   */
  redraw() {
    if (!this.shadowRoot || !this.container) return;

    const template = document.createElement('template');
    const html = this.template();

    // Render and append styles
    this.shadowRoot.innerHTML = '';
    this.hasStyles = false;
    this.appendStyles();
    template.innerHTML = html;
    this.shadowRoot.appendChild(template.content.cloneNode(true));
    this.container = this.shadowRoot.querySelector<HTMLElement>('.ids-card');

    if (this.height) {
      const link = this.container?.querySelector<IdsHyperlink>('ids-hyperlink')?.container;
      this.setAttribute(attributes.HEIGHT, this.height);
      this.container?.style.setProperty('height', `${this.height}px`);
      link?.style.setProperty('height', `${this.height}px`);
      this.querySelector('[slot]')?.classList.add('fixed-height');
    }
  }

  /**
   * Set the card to auto fit to its parent size
   * @param {boolean|null} value The auto fit
   */
  set autoFit(value) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.AUTO_FIT, String(value));
      return;
    }
    this.removeAttribute(attributes.AUTO_FIT);
  }

  /**
   * Get the auto fit state
   * @returns {boolean} true if the card auto fits to its parent size
   */
  get autoFit() { return stringToBool(this.getAttribute(attributes.AUTO_FIT)); }

  /**
   * Set the card to auto height
   * @param {boolean|null} value The height can be auto to contents
   */
  set autoHeight(value) {
    const val = stringToBool(value);
    if (stringToBool(value)) {
      this.setAttribute('auto-height', String(val));
      return;
    }
    this.removeAttribute('auto-height');
  }

  /**
   * Get the auto height state
   * @returns {string} the auto height attribute value
   */
  get autoHeight() { return this.getAttribute(attributes.AUTO_HEIGHT); }

  /**
   * Set the card to be actionable button.
   * @param {boolean | null} value The card can act as a button.
   */
  set actionable(value) {
    const val = stringToBool(value);
    if (stringToBool(value)) {
      this.setAttribute(attributes.ACTIONABLE, String(val));
      if (this.container) this.redraw();
      return;
    }
    this.removeAttribute(attributes.ACTIONABLE);
    if (this.container) this.redraw();
  }

  /**
   * Get the actionable state
   * @returns {boolean} true if the card is actionable
   */
  get actionable() { return stringToBool(this.getAttribute(attributes.ACTIONABLE)); }

  /**
   * Set the disabled state
   * @param {boolean | string} value to be disabled
   */
  set disabled(value: boolean | string) {
    this.toggleAttribute(attributes.DISABLED, stringToBool(value));

    if (this.disabled) {
      this.offEvent('mousemove', window.document);
      this.offEvent('click', window.document);
    }
  }

  /**
   * Get the disabled state
   * @returns {boolean | string} disabled state
   */
  get disabled(): boolean | string {
    return stringToBool(this.getAttribute(attributes.DISABLED));
  }

  /**
   * Set the draggable state
   * @param {boolean | string} value whether the card is draggable
   */
  set draggable(value: boolean | string) {
    this.toggleAttribute(attributes.DRAGGABLE, stringToBool(value));
  }

  /**
   * Get the draggable state
   * @returns {boolean | string} true if the card is draggable
   */
  get draggable(): boolean {
    return this.hasAttribute(attributes.DRAGGABLE);
  }

  /**
   * Set the dropped state
   * @param {boolean} value whether the card is dropped
   */
  set dropped(value: boolean) {
    this.toggleAttribute(attributes.DROPPED, stringToBool(value));
    this.container?.classList.toggle('is-dropped', stringToBool(value));
  }

  /**
   * Get the dropped state
   * @returns {boolean} true if the card is dropped
   */
  get dropped() {
    return this.hasAttribute(attributes.DROPPED);
  }

  /**
   * Set the drop target element ID
   * @param {string | null} value the ID of the drop target element
   */
  set dropTarget(value: string | null) {
    if (value) {
      this.setAttribute(attributes.DROP_TARGET, value);
    } else {
      this.removeAttribute(attributes.DROP_TARGET);
    }
  }

  /**
   * Get the drop target element ID
   * @returns {string | null} the ID of the drop target element
   */
  get dropTarget() {
    return this.getAttribute(attributes.DROP_TARGET);
  }

  /**
   * Set the fixed state
   * @param {boolean | string} value whether the card is fixed in position
   */
  set fixed(value: boolean | string) {
    this.toggleAttribute(attributes.FIXED, stringToBool(value));
    this.container?.classList.toggle('is-fixed', stringToBool(value));
  }

  /**
   * Get the fixed state
   * @returns {boolean | string} true if the card is fixed in position
   */
  get fixed(): boolean | string {
    return this.hasAttribute(attributes.FIXED);
  }

  /**
   * Set the stacked state
   * @param {boolean | string} value whether cards can be stacked
   */
  set stacked(value: boolean | string) {
    this.toggleAttribute(attributes.STACKED, stringToBool(value));
  }

  /**
   * Get the stacked state
   * @returns {boolean} true if cards can be stacked
   */
  get stacked(): boolean | string {
    return this.hasAttribute(attributes.STACKED);
  }

  #setCssVar(variable: string, value: string | null) {
    if (value) {
      this.container?.style.setProperty(variable, value);
    } else {
      this.container?.style.removeProperty(variable);
    }
  }

  /**
   * Set the background color
   * @param {IdsColorValue} value the background color value
   */
  set backgroundColor(value: IdsColorValue) {
    this.#setCssVar('--ids-card-color-background', (value as string | null));
  }

  /**
   * Set the width of the card
   * @param {string} value the width value
   */
  set width(value: string) {
    if (this.draggable) {
      this.#setCssVar('--ids-card-width', value);
    } else {
      super.width = value;
    }
  }

  /**
   * Set how the container overflows, can be hidden or auto (default)
   * @param {string | null} [value] css property for overflow
   */
  set overflow(value) {
    if (value === 'hidden') {
      this.container?.querySelector('.ids-card-content')?.classList.add('overflow-hidden');
      this.setAttribute(attributes.OVERFLOW, value);
    } else {
      this.container?.querySelector('.ids-card-content')?.classList.remove('overflow-hidden');
      this.removeAttribute(attributes.OVERFLOW);
    }
  }

  /**
   * Get the overflow setting
   * @returns {string | null} the overflow attribute value
   */
  get overflow() { return this.getAttribute(attributes.OVERFLOW); }

  /**
   * Get href for actionable link card
   * @returns {string} href for ids-hyperlink
   */
  get href() { return this.getAttribute('href'); }

  /**
   * Set href for actionable link card
   * @param {string} url href for ids-hyperlink
   */
  set href(url) {
    if (url) {
      this.setAttribute('href', url);
      if (this.container) {
        this.redraw();
        this.container.querySelector('ids-hyperlink')?.setAttribute('href', url);
      }
    } else {
      this.removeAttribute('href');
      if (this.container) {
        this.redraw();
      }
    }
  }

  /**
   * Get target for actionable link card
   * @returns {string} target for ids-hyperlink
   */
  get target() { return this.getAttribute('target'); }

  /**
   * Set target for an actionable link card
   * @param {string} value target value for ids-hyperlink
   */
  set target(value) {
    if (value) {
      this.setAttribute('target', value);
      if (this.container) {
        this.container.querySelector('ids-hyperlink')?.setAttribute('target', value);
        this.redraw();
      }
    } else {
      this.removeAttribute('target');
      if (this.container) {
        this.redraw();
      }
    }
  }

  /**
   * Get the no header state
   * @returns {string | null} state of the header
   */
  get noHeader() { return this.getAttribute(attributes.NO_HEADER); }

  /**
   * Set to true to hide the header space
   * @param {boolean | string} value whether to hide the header
   */
  set noHeader(value) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.NO_HEADER, 'true');
    } else {
      this.removeAttribute(attributes.NO_HEADER);
    }
  }
}
